import { useMemo } from 'react';

import { PrintProps } from './multiple-choice.constants';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import {
  MultipleChoiceLetter,
  MultipleChoiceLetterImage,
  MultipleChoiceStatus,
} from './multiple-choice';
import {
  canShowOptionLetter,
  getOptionsPerColumn,
  getRowAndColumnsValue,
  parseMultipleChoiceTypes,
  sortOptions,
} from './multiple-choice.utils';
import MultipleChoiceImagePrint from './image/multiple-choice-image-print';
import MultipleChoiceTextImagePrint from './text-image/multiple-choice-text-image-print';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import MultipleChoicePrintAnswersList from './list/multiple-choice-print-answers-list';
import * as Styled from './multiple-choice-print.style';
import { useActivityAnswersPrintContext } from 'approot/activity-builder/print/answers/activity-answers-print.context';
import { getOptionStatus } from './logic/get-option-status';

const MultipleChoicePrintAnswers = (
  props: PrintProps & {
    previewSize?: number;
    studentId?: number;
  }
) => {
  const { properties, subActivityProperties, previewSize, studentId } = props;
  const { multipleChoice } = properties;

  const { answers: sourceAnswers, showAnswer } = useActivityAnswersPrintContext(
    props.id
  );

  const answers = useMemo(
    () =>
      studentId
        ? sourceAnswers?.filter(a => a.studentId === studentId)
        : sourceAnswers,
    [sourceAnswers, studentId]
  );

  const chosenAnswers = useMemo(
    () =>
      (answers?.[0]?.answer as MultipleChoiceAnswerFragment)
        ?.multipleChoiceItems || [],
    [answers]
  );

  const hasAnswered = useMemo(() => {
    return chosenAnswers.length > 0;
  }, [chosenAnswers]);

  const { isList, isText, isImage, isTextAndImage, isFree } =
    parseMultipleChoiceTypes(properties);

  const sortedOptions = useMemo(
    () => sortOptions(multipleChoice.options),
    [multipleChoice.options]
  );

  if (isText && isList) {
    return (
      <MultipleChoicePrintAnswersList
        {...props}
        answers={chosenAnswers}
        showAnswer={showAnswer}
      />
    );
  }

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Styled.MultipleChoiceButtons
        role="radiogroup"
        $display={properties.layout?.display}
        $isNoWrap={
          !!(
            multipleChoice.rowsGridLayout &&
            getRowAndColumnsValue(multipleChoice.rowsGridLayout).rows === 1
          )
        }
      >
        {sortedOptions.map((option, index) => {
          const isOptionSelected = !!chosenAnswers.find(
            ca => ca.itemId === option.itemId && ca.isSelected
          );

          const { isCorrect, isIncorrect, isSelected } = getOptionStatus({
            hasAnswered,
            showAnswer: showAnswer,
            isOptionSelected,
            option,
          });

          const optionsPerColumn = getOptionsPerColumn(
            properties.multipleChoice
          );

          return (
            <Styled.MultipleChoiceItem
              key={option.itemId}
              $isText={isText}
              $itemsPerRow={optionsPerColumn}
              $minHeight={multipleChoice.optionMinHeight || 80}
              $hasMarginRight={(index + 1) % optionsPerColumn !== 0}
              $isCorrect={isCorrect}
              $isIncorrect={isIncorrect}
              $isSelected={isSelected}
              $isDisabled={hasAnswered && !isSelected}
            >
              {!isFree && (
                <MultipleChoiceStatus
                  isCorrect={hasAnswered ? isCorrect : undefined}
                  isIncorrect={hasAnswered ? isIncorrect : undefined}
                />
              )}

              {isTextAndImage ? (
                <MultipleChoiceTextImagePrint
                  optionMinHeight={multipleChoice.optionMinHeight}
                  imagePosition={multipleChoice.imagePosition}
                  showOptionLetter={multipleChoice.showOptionLetter}
                  option={option}
                  index={index}
                  previewSize={previewSize}
                />
              ) : (
                <Styled.MultipleChoicePrintContent>
                  {isImage ? (
                    <Styled.MultipleChoicePrintImage>
                      <MultipleChoiceImagePrint
                        option={option}
                        previewSize={previewSize}
                      />
                      {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                        <MultipleChoiceLetterImage index={index} />
                      )}
                    </Styled.MultipleChoicePrintImage>
                  ) : (
                    <Styled.MultipleChoicePrintTextContent>
                      {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                        <MultipleChoiceLetter index={index} />
                      )}
                      <Styled.MultipleChoiceButtonText>
                        {option.text}
                      </Styled.MultipleChoiceButtonText>
                    </Styled.MultipleChoicePrintTextContent>
                  )}
                </Styled.MultipleChoicePrintContent>
              )}
            </Styled.MultipleChoiceItem>
          );
        })}
      </Styled.MultipleChoiceButtons>
    </SubActivityPrint>
  );
};

export default MultipleChoicePrintAnswers;
